import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { 
  Download, FileText, TrendingUp, DollarSign, Users, BarChart3, Calendar,
  FileSpreadsheet, Building2, UserCheck, Clock, Target, Briefcase,
  PieChart as PieChartIcon, LineChart as LineChartIcon, ArrowUpRight, ArrowDownRight,
  AlertTriangle, CheckCircle2, XCircle, RefreshCw, Crown, Lock, Sparkles,
  Calculator, TrendingDown, Percent, Shield, Heart, Eye, Activity,
  type LucideIcon
} from "lucide-react";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  LineChart, Line, PieChart, Pie, Cell, Area, AreaChart, Legend, ComposedChart
} from "recharts";
import type {
  Client, Plan, Enrollment, Commission, Task, Appointment, Quote
} from "@shared/schema";
import { isHealthOrMedicalPlan, getSicCodeLabel } from "@shared/schema";
import {
  RENEWAL_PIPELINE_BUCKETS, calendarDaysUntil, renewalPipelineBucket, resolveNextRenewalByClient,
  type ClientPlanRenewalInput,
} from "@shared/utils/renewals";
import { toLocalDateKey } from "@shared/lib/taskStatus";
import { exportToCSV } from "@/lib/exportUtils";
import { pluralize } from "@/lib/pluralize";
import { sumActiveEmployees, summarizeActiveClientPlans } from "@/lib/reportSummary";
import {
  avgBookSize, avgCloseDays, avgTenureYears, crossSellGaps, premiumGrowthRate, retentionRate,
} from "@/lib/advancedReportMetrics";
import { apiRequest } from "@/lib/queryClient";
import { format, subMonths, differenceInDays, startOfMonth, endOfMonth, addMonths } from "date-fns";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";
import {
  bookEnrollments, buildBookPremiumContext, sumMonthlyPremium, sumMonthlyPremiumByClient, sumYtdRevenue, ytdRevenueByMonth,
} from "@shared/utils/bookPremium";

/** Advanced-tab value with no data source (or nothing to compute from yet). */
function NotEnoughData({ reason }: { reason: string }) {
  return (
    <span className="text-right" title={reason}>
      <span className="text-lg font-bold text-muted-foreground">—</span>
      <span className="block text-xs text-muted-foreground">Not enough data</span>
    </span>
  );
}

/** Prorated revenue is kept unrounded; round to cents only here, for display. */
const toCents = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

type ReportType = 
  | "book-of-business"
  | "commission-statement"
  | "renewal-pipeline"
  | "production-report"
  | "client-census"
  | "enrollment-activity"
  | "premium-summary"
  | "carrier-distribution";

interface ReportConfig {
  id: ReportType;
  name: string;
  description: string;
  icon: LucideIcon;
  category: "clients" | "financial" | "operations";
}

// keep in sync with GET /api/commissions permission check (server/routes/comissionRoutes.ts)
const COMMISSION_PERMISSIONS = [
  "commissions:view", "agency_commissions:view", "agency_commissions:manage",
  "agency_commissions:reports", "commissions:manage", "commissions:reports",
];

const REPORT_CONFIGS: ReportConfig[] = [
  {
    id: "book-of-business",
    name: "Book of Business",
    description: "Complete client roster with policies, premiums, and renewal dates",
    icon: Building2,
    category: "clients"
  },
  {
    id: "commission-statement",
    name: "Commission Statement",
    description: "Detailed commission breakdown by client, carrier, and plan type",
    icon: DollarSign,
    category: "financial"
  },
  {
    id: "renewal-pipeline",
    name: "Renewal Pipeline",
    description: "Upcoming renewals with days until expiry and revenue at risk",
    icon: Calendar,
    category: "operations"
  },
  {
    id: "production-report",
    name: "Production Report",
    description: "New business vs renewals by producer with YoY comparison",
    icon: TrendingUp,
    category: "financial"
  },
  {
    id: "client-census",
    name: "Client Census",
    description: "Employee headcount across all active clients, by state",
    icon: Users,
    category: "clients"
  },
  {
    id: "enrollment-activity",
    name: "Enrollment Activity",
    description: "New, terminated, and changed enrollments by date range",
    icon: Activity,
    category: "operations"
  },
  {
    id: "premium-summary",
    name: "Premium Summary",
    description: "Premium volume by carrier, plan type, and coverage tier",
    icon: BarChart3,
    category: "financial"
  },
  {
    id: "carrier-distribution",
    name: "Carrier Distribution",
    description: "Business mix by carrier with historical trends",
    icon: PieChartIcon,
    category: "operations"
  }
];

// Subset of GET /api/tasks/stats used by Task Performance
interface TaskStatsWithPeriod {
  overdue: number;
  period?: { completed: number; total: number };
}

// Mirrors RENEWAL_STAGES in Renewals.tsx — same RENEWAL_STAGE_KEYS values, display labels only
const RENEWAL_STAGE_LABELS: Record<string, string> = {
  not_started: "Not Started",
  rfp_out: "RFP Out",
  proposals_in: "Proposals In",
  presented: "Presented",
  uw_review: "UW Review",
  open_enrollment: "In Open Enrollment",
  complete: "Complete",
};

// keep in sync with status badge mapping in CommissionManagementTab.tsx
const COMMISSION_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  complete: "Complete",
};

export default function Reports() {
  const [reportPeriod, setReportPeriod] = useState("month");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [generatingReport, setGeneratingReport] = useState<ReportType | null>(null);
  const { toast } = useToast();
  const { hasFeature, tier } = useSubscriptionTier();
  const { canAny } = usePermissions();
  // gates both the Reports-tab cards and Dashboard-tab widgets below; see
  // docs/REPORTS_ACCESS_CONTROL.md for the per-role permission grants this assumes
  const canSeeFinancialReports = canAny(COMMISSION_PERMISSIONS);
  const canSeeGeneralReports = canAny(["reports:view"]);

  function parseMoney(value: unknown) {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value !== "string") return 0;
    const normalized = value.replace(/[^0-9.-]/g, "");
    const num = Number(normalized);
    return Number.isFinite(num) ? num : 0;
  }

  function parseDate(value: unknown) {
    if (!value) return undefined;
    const d = value instanceof Date ? value : new Date(String(value));
    return Number.isFinite(d.getTime()) ? d : undefined;
  }

  function getPeriodStart(period: string, now: Date) {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);

    if (period === "week") {
      start.setDate(start.getDate() - start.getDay());
      return start;
    }

    if (period === "month") {
      start.setDate(1);
      return start;
    }

    if (period === "quarter") {
      const qStartMonth = Math.floor(start.getMonth() / 3) * 3;
      start.setMonth(qStartMonth, 1);
      return start;
    }

    start.setMonth(0, 1);
    return start;
  }

  function isInSelectedPeriod(dateValue: unknown) {
    const now = new Date();
    const d = parseDate(dateValue);
    if (!d) return false;
    const start = getPeriodStart(reportPeriod, now);
    return d >= start && d <= now;
  }

  const periodLabel =
    reportPeriod === "week" ? "week" : reportPeriod === "month" ? "month" : reportPeriod === "quarter" ? "quarter" : "year";

  const { data: clients = [] } = useQuery<Client[]>({ queryKey: ["/api/clients"] });
  // enabled: matches the permission each endpoint actually requires — without it,
  // a role missing one of these still fires the request, 403s, and triggers the
  // global permission-denied toast (queryClient.ts) for no reason a user can act on
  const { data: prospects = [] } = useQuery<any[]>({ queryKey: ["/api/prospects"], enabled: canAny(["prospects:view"]) });
  const { data: plans = [] } = useQuery<Plan[]>({ queryKey: ["/api/plans"], enabled: canAny(["plans:view"]) });
  const { data: enrollments = [] } = useQuery<Enrollment[]>({ queryKey: ["/api/enrollments"] });
  const { data: clientPlans = [] } = useQuery<ClientPlanRenewalInput[]>({ queryKey: ["/api/clientplans"] });
  const { data: commissions = [] } = useQuery<Commission[]>({ queryKey: ["/api/commissions"] });
  // Task Performance is counted server-side (same scope as the badge and /tasks):
  // a page of /api/tasks caps at 100 rows and undercounted busy agencies (DEV-331).
  // periodStart/today are the browser's local period start and calendar day.
  const taskStatsParams = useMemo(() => {
    const now = new Date();
    return {
      periodStart: getPeriodStart(reportPeriod, now).toISOString(),
      today: toLocalDateKey(now),
    };
  }, [reportPeriod]);
  const { data: taskStats } = useQuery<TaskStatsWithPeriod>({
    queryKey: ["/api/tasks/stats", taskStatsParams],
    enabled: canSeeGeneralReports,
  });
  const { data: appointments = [] } = useQuery<Appointment[]>({ queryKey: ["/api/appointments"] });
  const { data: quotes = [] } = useQuery<Quote[]>({ queryKey: ["/api/quotes"], enabled: canAny(["quotes:view"]) });
  const { data: carriers = [] } = useQuery<any[]>({ queryKey: ["/api/carriers"], enabled: canAny(["carriers:view"]) });
  const { data: employees = [] } = useQuery<any[]>({ queryKey: ["/api/employees"] });

  function toYearMonth(d: Date) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
  }

  function getIncludedYearMonths(period: string, now: Date) {
    const included = new Set<string>();
    const end = new Date(now);
    end.setHours(0, 0, 0, 0);
    const endYear = end.getFullYear();
    const endMonth = end.getMonth();

    if (period === "month" || period === "week") {
      included.add(toYearMonth(end));
      return included;
    }

    if (period === "quarter") {
      const qStartMonth = Math.floor(endMonth / 3) * 3;
      for (let m = qStartMonth; m <= endMonth; m++) {
        included.add(`${endYear}-${String(m + 1).padStart(2, "0")}`);
      }
      return included;
    }

    for (let m = 0; m <= endMonth; m++) {
      included.add(`${endYear}-${String(m + 1).padStart(2, "0")}`);
    }
    return included;
  }

  const filteredClients = clients.filter(c => isInSelectedPeriod((c as any).createdAt));
  const filteredEnrollments = enrollments.filter(e =>
    isInSelectedPeriod((e as any).effectiveDate ?? (e as any).createdAt)
  );
  // In-force book premium: same rule /renewals uses (shared/utils/bookPremium.ts, DEV-327).
  // Every premium figure below groups bookRows, so plan-type / carrier / CSV
  // totals add up to the Monthly Total. Only the annual-revenue figures (the
  // "Annual Premium (YTD)" tile and the Production Report) differ: calendar year
  // to date, where groups that left earlier this year keep their months, and
  // a month started or ended mid-month is prorated by day (shown to the cent).
  const bookCtx = useMemo(() => buildBookPremiumContext({ clients, plans: plans as any[] }), [clients, plans]);
  const bookRows = useMemo(() => bookEnrollments(enrollments, bookCtx), [enrollments, bookCtx]);
  const ytdRevenue = useMemo(() => sumYtdRevenue(enrollments, bookCtx), [enrollments, bookCtx]);
  const allTimeMonthlyPremiums = useMemo(() => sumMonthlyPremium(bookRows), [bookRows]);
  const premiumByClient = useMemo(() => sumMonthlyPremiumByClient(bookRows), [bookRows]);
  const includedCommissionMonths = getIncludedYearMonths(reportPeriod, new Date());
  const filteredCommissions = commissions.filter(c => {
    const monthKey = String((c as any).accountingMonth || (c as any).month || "");
    if (monthKey) return includedCommissionMonths.has(monthKey);
    return isInSelectedPeriod((c as any).createdAt ?? (c as any).updatedAt);
  });
  // Completed = resolved/closed within the period (by resolution date, so a task
  // due last month resolved today counts). Overdue = open and due before today,
  // regardless of period. See GET /api/tasks/stats.
  const periodTaskTotal = taskStats?.period?.total ?? 0;
  const completedTasksThisPeriod = taskStats?.period?.completed ?? 0;
  const overdueTaskCount = taskStats?.overdue ?? 0;
  const filteredAppointments = appointments.filter(a => isInSelectedPeriod((a as any).startTime));
  const filteredQuotes = quotes.filter(q => isInSelectedPeriod((q as any).createdAt));

  // /api/employees here is agency staff only — count client employees (DEV-357)
  const totalActiveEmployees = useMemo(() => sumActiveEmployees(clients as any[]), [clients]);

  const reportMetrics = useMemo(() => ({
    totalClients: clients.length,
    activeClients: clients.filter(c => c.status === "active").length,
    newClientsThisPeriod: filteredClients.length,
    totalProspects: prospects.length,
    // Book × 12 (Revenue Forecast, Avg Book Size)
    annualPremiumValue: allTimeMonthlyPremiums * 12,
    // Premium billed Jan 1 through today (annual-revenue rule, not the period toggle)
    ytdPremiumValue: ytdRevenue,
    periodEnrollments: filteredEnrollments.length,
    totalCommissions: filteredCommissions.reduce(
      (sum, c) => sum + parseMoney((c as any).actualCommission ?? (c as any).commissionAmount), 0
    ),
    totalEnrollments: enrollments.length,
    activeEnrollments: enrollments.filter(e => e.status === "active").length,
    totalQuotes: filteredQuotes.length,
    acceptedQuotes: filteredQuotes.filter(q => q.status === "accepted").length,
    pendingQuotes: filteredQuotes.filter(q => q.status === "sent" || q.status === "draft").length,
    conversionRate: filteredQuotes.filter(q => q.status === "sent").length > 0 
      ? (filteredQuotes.filter(q => q.status === "accepted").length / filteredQuotes.filter(q => q.status === "sent").length * 100)
      : 0,
    totalTasks: periodTaskTotal,
    completedTasks: completedTasksThisPeriod,
    overdueTasks: overdueTaskCount,
    taskCompletionRate: periodTaskTotal > 0
      ? Math.min(100, completedTasksThisPeriod / periodTaskTotal * 100)
      : 0,
    totalAppointments: filteredAppointments.length,
    upcomingAppointments: filteredAppointments.filter(a => new Date(a.startTime) > new Date()).length,
    avgPremiumPerClient: clients.length > 0 ? allTimeMonthlyPremiums / clients.length : 0,
    avgPremiumPerEmployee: totalActiveEmployees > 0 ? allTimeMonthlyPremiums / totalActiveEmployees : 0,
  }), [clients, prospects, enrollments, filteredEnrollments, filteredCommissions, filteredQuotes, periodTaskTotal, completedTasksThisPeriod, overdueTaskCount, filteredAppointments, totalActiveEmployees, allTimeMonthlyPremiums, ytdRevenue, filteredClients]);

  // /api/plans includes unassigned agency-level plans and /api/carriers is unscoped
  // platform-wide; library plans have no clientId — count active client_plans
  // links of the clients this user can see instead (DEV-357).
  const activeClientPlanSummary = useMemo(
    () => summarizeActiveClientPlans(clientPlans as any[], new Set(clients.map(c => c.id))),
    [clientPlans, clients]
  );

  // Advanced tab (DEV-332): null → "—" / Not enough data
  const advancedMetrics = useMemo(() => {
    const now = new Date();
    return {
      retention: retentionRate(clients as any[], getPeriodStart(reportPeriod, now)),
      avgTenure: avgTenureYears(clients as any[], now),
      // same monthly figures as the Production Report
      growth: premiumGrowthRate(ytdRevenueByMonth(enrollments, bookCtx), reportPeriod, now),
      avgCloseDays: avgCloseDays(prospects, clients as any[]),
      avgBookSize: avgBookSize(reportMetrics.annualPremiumValue, reportMetrics.activeClients),
      crossSell: crossSellGaps(clients as any[], clientPlans as any[]),
    };
  }, [clients, clientPlans, enrollments, bookCtx, prospects, reportPeriod, reportMetrics]);

  // Trend-chart window follows the selected report period (year: 12 months,
  // quarter: 3, month/week: fall back to 3 so a trend is still visible).
  const chartMonths =
    reportPeriod === "year" ? 12 : reportPeriod === "quarter" ? 3 : 3;

  const clientGrowthData = useMemo(() => Array.from({ length: chartMonths }, (_, i) => {
    const month = subMonths(new Date(), chartMonths - 1 - i);
    const monthClients = clients.filter(c => {
      const clientDate = new Date(c.createdAt || "");
      return clientDate.getMonth() === month.getMonth() && clientDate.getFullYear() === month.getFullYear();
    }).length;
    
    return {
      month: format(month, "MMM"),
      fullMonth: format(month, "MMM yyyy"),
      clients: monthClients,
      cumulative: clients.filter(c => {
        const clientDate = new Date(c.createdAt || "");
        return clientDate <= month;
      }).length
    };
  }), [clients, chartMonths]);

  const revenueByType = useMemo(() => {
    const typeMap: Record<string, { revenue: number; enrollments: number; name: string }> = {};
    
    bookRows.forEach(e => {
      const plan = plans.find(p => p.id === e.planId);
      const type = plan?.type || "other";
      const typeName = type === "health" ? "Health" : type === "dental" ? "Dental" : type === "vision" ? "Vision" : type === "life" ? "Life" : "Other";
      
      if (!typeMap[type]) {
        typeMap[type] = { revenue: 0, enrollments: 0, name: typeName };
      }
      typeMap[type].revenue += Number(e.totalPremium || 0);
      typeMap[type].enrollments += 1;
    });
    
    return Object.entries(typeMap)
      .map(([type, data]) => ({ type, ...data }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [bookRows, plans]);

  const carrierDistribution = useMemo(() => {
    const carrierMap: Record<string, { revenue: number; clients: number; enrollments: number; name: string }> = {};

    bookRows.forEach(e => {
      const plan = plans.find(p => p.id === e.planId);
      const carrier = carriers.find(c => c.id === plan?.carrierId);
      const carrierName = carrier?.name || "Unknown";
      
      if (!carrierMap[carrierName]) {
        carrierMap[carrierName] = { revenue: 0, clients: 0, enrollments: 0, name: carrierName };
      }
      carrierMap[carrierName].revenue += Number(e.totalPremium || 0);
      carrierMap[carrierName].enrollments += 1;
    });
    
    // every carrier, so the CSV adds up to the book; the dashboard card shows its top 4
    return Object.values(carrierMap).sort((a, b) => b.revenue - a.revenue);
  }, [bookRows, plans, carriers]);

  const commissionTrends = useMemo(() => {
    const months: Record<string, { total: number; month: string }> = {};
    
    const window = Math.max(chartMonths, 3);
    for (let i = window - 1; i >= 0; i--) {
      const month = subMonths(new Date(), i);
      const key = format(month, "yyyy-MM");
      months[key] = { total: 0, month: format(month, "MMM") };
    }
    
    commissions.forEach(c => {
      const monthKey = String((c as any).accountingMonth || (c as any).month || "");
      if (months[monthKey]) {
        months[monthKey].total += parseMoney((c as any).actualCommission ?? (c as any).commissionAmount);
      }
    });
    
    return Object.values(months);
  }, [commissions, chartMonths]);

  const renewalPipeline = useMemo(() => {
    const now = new Date();
    const upcoming = RENEWAL_PIPELINE_BUCKETS.map(range => ({ range: range as string, count: 0, revenue: 0 }));

    // Renewal dates live on client plans, not clients — same resolution as
    // the Dashboard card and /renewals (active clients, date-only).
    const activeClientIds = new Set(clients.filter(c => c.status === "active").map(c => c.id));
    const nextRenewals = resolveNextRenewalByClient(clientPlans, { today: now, clientIds: activeClientIds });

    nextRenewals.forEach((renewalDate, clientId) => {
      const bucket = upcoming[renewalPipelineBucket(calendarDaysUntil(renewalDate, now))];
      const clientPremium = premiumByClient.get(clientId) ?? 0;
      bucket.count++;
      bucket.revenue += clientPremium;
    });

    return upcoming;
  }, [clients, clientPlans, premiumByClient]);

  const pipelineMetrics = useMemo(() => {
    const stages = [
      { name: "Prospects", count: prospects.length, value: prospects.reduce((sum, p) => sum + Number((p as any).estimatedPremium || 0), 0) },
      { name: "Quotes Sent", count: quotes.filter(q => q.status === "sent").length, value: quotes.filter(q => q.status === "sent").reduce((sum, q) => sum + Number((q as any).totalMonthlyPremium || 0), 0) },
      { name: "Accepted", count: quotes.filter(q => q.status === "accepted").length, value: quotes.filter(q => q.status === "accepted").reduce((sum, q) => sum + Number((q as any).totalMonthlyPremium || 0), 0) },
      { name: "Active Clients", count: clients.filter(c => c.status === "active").length, value: allTimeMonthlyPremiums },
    ];
    return stages;
  }, [prospects, quotes, clients, allTimeMonthlyPremiums]);

  const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4", "#EC4899", "#84CC16"];

  const generateReport = async (reportType: ReportType) => {
    setGeneratingReport(reportType);
    
    try {
      let reportData: any[] = [];
      let headers: string[] = [];
      let filename = "";
      
      switch (reportType) {
        case "book-of-business":
          headers = ["Client Name", "Status", "Industry", "Employee Count", "Monthly Premium", "Annual Premium", "Acquired Date", "Renewal Date", "Primary Contact", "Email"];
          reportData = clients.map(c => {
            const clientPremium = premiumByClient.get(c.id) ?? 0;
            // Renewal Date is a per-plan field — pull it from this client's medical plan (most recent)
            const medicalPlan = plans
              .filter(p => p.clientId === c.id && isHealthOrMedicalPlan((p as any).type) && (p as any).isActive !== false)
              .sort((a, b) => new Date((b as any).effectiveDate || 0).getTime() - new Date((a as any).effectiveDate || 0).getTime())[0];
            const industryLabel = (c as any).sicCodeId
              ? getSicCodeLabel((c as any).sicCodeId)
              : (c as any).sic || "—";
            return {
              "Client Name": c.name,
              "Status": c.status,
              "Industry": industryLabel,
              "Employee Count": (c as any).actualEmployeeCount ?? "—",
              "Monthly Premium": `$${clientPremium.toLocaleString()}`,
              "Annual Premium": `$${(clientPremium * 12).toLocaleString()}`,
              // clientSince is often unset — fall back to row creation date
              "Acquired Date": (c as any).clientSince || c.createdAt ? format(new Date((c as any).clientSince || c.createdAt), "MM/dd/yyyy") : "—",
              "Renewal Date": (medicalPlan as any)?.renewalDate ? format(new Date((medicalPlan as any).renewalDate), "MM/dd/yyyy") : "—",
              "Primary Contact": (c as any).primaryContactName || "—",
              "Email": (c as any).contactEmail || "—"
            };
          });
          filename = `book-of-business-${format(new Date(), "yyyy-MM-dd")}`;
          break;
          
        case "commission-statement":
          headers = ["Period", "Period Paid", "Client", "Carrier", "Plan Type", "Premium", "Commission Rate", "Commission Amount", "Status"];
          reportData = commissions.map(c => ({
            "Period": (c as any).month || "—",
            "Period Paid": (c as any).accountingMonth || "—",
            "Client": clients.find(cl => cl.id === (c as any).clientId)?.name || "—",
            "Carrier": carriers.find(ca => ca.id === (c as any).carrierId)?.name || "—",
            "Plan Type": (c as any).planType || "—",
            "Premium": `$${parseMoney((c as any).actualPremium ?? (c as any).projectedPremium).toLocaleString()}`,
            "Commission Rate": `${(c as any).commissionRate || 0}%`,
            "Commission Amount": `$${parseMoney((c as any).actualCommission ?? (c as any).commissionAmount).toLocaleString()}`,
            "Status": COMMISSION_STATUS_LABELS[(c as any).status] || COMMISSION_STATUS_LABELS.pending
          }));
          filename = `commission-statement-${format(new Date(), "yyyy-MM-dd")}`;
          break;
          
        case "renewal-pipeline": {
          headers = ["Client Name", "Renewal Date", "Days Until Renewal", "Stage", "Urgency", "Monthly Premium", "Annual Premium at Risk", "Primary Contact"];
          const now = new Date();
          reportData = clients
            .map(c => {
              // Renewal Date is a per-plan field — pull it from this client's medical plan (most recent)
              const medicalPlan = plans
                .filter(p => p.clientId === c.id && isHealthOrMedicalPlan((p as any).type) && (p as any).isActive !== false)
                .sort((a, b) => new Date((b as any).effectiveDate || 0).getTime() - new Date((a as any).effectiveDate || 0).getTime())[0];
              return { c, renewalDate: (medicalPlan as any)?.renewalDate ? new Date((medicalPlan as any).renewalDate) : null };
            })
            .filter((row): row is { c: typeof clients[number]; renewalDate: Date } => row.renewalDate !== null)
            .map(({ c, renewalDate }) => {
              const days = differenceInDays(renewalDate, now);
              const clientPremium = premiumByClient.get(c.id) ?? 0;
              return {
                "Client Name": c.name,
                "Renewal Date": format(renewalDate, "MM/dd/yyyy"),
                "Days Until Renewal": days,
                "Stage": RENEWAL_STAGE_LABELS[(c as any).renewalStage] || (c as any).renewalStage || "—",
                "Urgency": days < 0 ? "Expired" : days <= 30 ? "Urgent" : days <= 60 ? "Upcoming" : "Scheduled",
                "Monthly Premium": `$${clientPremium.toLocaleString()}`,
                "Annual Premium at Risk": `$${(clientPremium * 12).toLocaleString()}`,
                "Primary Contact": (c as any).primaryContactName || "—"
              };
            })
            .sort((a, b) => a["Days Until Renewal"] - b["Days Until Renewal"]);
          filename = `renewal-pipeline-${format(new Date(), "yyyy-MM-dd")}`;
          break;
        }
          
        case "production-report":
          headers = ["Period", "New Business Count", "New Business Premium", "Renewals Count", "Renewals Premium", "Total Premium", "Growth %"];
          // Calendar year to date: Jan through this month. Total Premium is what
          // was in force each month (groups that left keep their months), so the
          // column sums to the Annual Premium (YTD) tile.
          const totalByMonth = ytdRevenueByMonth(enrollments, bookCtx);
          const year = new Date().getFullYear();
          reportData = totalByMonth.map((total, i) => {
            const month = new Date(year, i, 1);
            const monthStart = startOfMonth(month);
            const monthEnd = endOfMonth(month);
            const newBiz = clients.filter(c => {
              const created = new Date(c.createdAt || "");
              return created >= monthStart && created <= monthEnd;
            });
            const newBizIds = new Set(newBiz.map(c => c.id));
            const newBizPremium = ytdRevenueByMonth(enrollments.filter(e => newBizIds.has(e.clientId)), bookCtx)[i];

            return {
              "Period": format(month, "MMM yyyy"),
              "New Business Count": newBiz.length,
              "New Business Premium": `$${toCents(newBizPremium)}`,
              "Renewals Count": "—",
              "Renewals Premium": "—",
              "Total Premium": `$${toCents(total)}`,
              "Growth %": "—"
            };
          });
          filename = `production-report-${format(new Date(), "yyyy-MM-dd")}`;
          break;
          
        case "client-census": {
          headers = ["State", "Employee Count", "Active Clients", "Avg Employees per Client"];
          let censusRows: { id: number; clientId: number; state: string | null }[] = [];
          try {
            const res = await apiRequest("GET", "/api/employees/census");
            censusRows = await res.json();
          } catch (err) {
            toast({ title: "Failed to load employee census", variant: "destructive" });
            break;
          }
          const byState = new Map<string, { employeeCount: number; clientIds: Set<number> }>();
          const allActiveClientIds = new Set<number>();
          for (const row of censusRows) {
            allActiveClientIds.add(row.clientId);
            const stateKey = row.state || "Unknown";
            const bucket = byState.get(stateKey) || { employeeCount: 0, clientIds: new Set<number>() };
            bucket.employeeCount += 1;
            bucket.clientIds.add(row.clientId);
            byState.set(stateKey, bucket);
          }
          reportData = Array.from(byState.entries())
            .map(([state, { employeeCount, clientIds }]) => ({
              "State": state,
              "Employee Count": employeeCount,
              "Active Clients": clientIds.size,
              "Avg Employees per Client": (employeeCount / clientIds.size).toFixed(1)
            }))
            .sort((a, b) => b["Employee Count"] - a["Employee Count"]);
          if (censusRows.length > 0) {
            reportData.push({
              "State": "All States",
              "Employee Count": censusRows.length,
              "Active Clients": allActiveClientIds.size,
              "Avg Employees per Client": (censusRows.length / allActiveClientIds.size).toFixed(1)
            });
          }
          filename = `client-census-${format(new Date(), "yyyy-MM-dd")}`;
          break;
        }
          
        case "enrollment-activity":
          headers = ["Date", "Employee", "Client", "Plan", "Action", "Coverage Tier", "Premium", "Effective Date"];
          reportData = enrollments
            .filter(e => isInSelectedPeriod((e as any).createdAt || (e as any).effectiveDate))
            .map(e => {
              const emp = employees.find(em => em.id === e.employeeId);
              const client = clients.find(c => c.id === e.clientId);
              const plan = plans.find(p => p.id === e.planId);
              return {
                "Date": (e as any).createdAt ? format(new Date((e as any).createdAt), "MM/dd/yyyy") : "—",
                "Employee": emp ? `${emp.firstName} ${emp.lastName}` : "—",
                "Client": client?.name || "—",
                "Plan": plan?.name || "—",
                "Action": e.status === "active" ? "Enrolled" : e.status === "terminated" ? "Terminated" : "Pending",
                "Coverage Tier": e.coverageTier || "—",
                "Premium": `$${Number(e.totalPremium || 0).toLocaleString()}`,
                "Effective Date": (e as any).effectiveDate ? format(new Date((e as any).effectiveDate), "MM/dd/yyyy") : "—"
              };
            });
          filename = `enrollment-activity-${format(new Date(), "yyyy-MM-dd")}`;
          break;
          
        case "premium-summary":
          headers = ["Carrier", "Plan Type", "Plan Name", "Enrollments", "Monthly Premium", "Annual Premium", "Avg Premium/Employee"];
          const planSummary: Record<string, any> = {};
          bookRows.forEach(e => {
            const plan = plans.find(p => p.id === e.planId);
            const carrier = carriers.find(c => c.id === plan?.carrierId);
            const key = `${carrier?.name || "Unknown"}-${plan?.type || "other"}-${plan?.name || "Unknown"}`;
            if (!planSummary[key]) {
              planSummary[key] = {
                carrier: carrier?.name || "Unknown",
                type: plan?.type || "other",
                name: plan?.name || "Unknown",
                enrollments: 0,
                premium: 0
              };
            }
            planSummary[key].enrollments++;
            planSummary[key].premium += Number(e.totalPremium || 0);
          });
          reportData = Object.values(planSummary).map((s: any) => ({
            "Carrier": s.carrier,
            "Plan Type": s.type,
            "Plan Name": s.name,
            "Enrollments": s.enrollments,
            "Monthly Premium": `$${s.premium.toLocaleString()}`,
            "Annual Premium": `$${(s.premium * 12).toLocaleString()}`,
            "Avg Premium/Employee": `$${(s.enrollments > 0 ? s.premium / s.enrollments : 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
          }));
          filename = `premium-summary-${format(new Date(), "yyyy-MM-dd")}`;
          break;
          
        case "carrier-distribution":
          headers = ["Carrier", "Clients", "Enrollments", "Monthly Premium", "Annual Premium", "Market Share %"];
          const totalPremium = allTimeMonthlyPremiums;
          reportData = carrierDistribution.map(cd => ({
            "Carrier": cd.name,
            "Clients": "—",
            "Enrollments": cd.enrollments,
            "Monthly Premium": `$${cd.revenue.toLocaleString()}`,
            "Annual Premium": `$${(cd.revenue * 12).toLocaleString()}`,
            "Market Share %": `${totalPremium > 0 ? ((cd.revenue / totalPremium) * 100).toFixed(1) : 0}%`
          }));
          filename = `carrier-distribution-${format(new Date(), "yyyy-MM-dd")}`;
          break;
      }
      
      if (reportData.length > 0) {
        exportToCSV(reportData, {
          filename,
          headers,
          toast: {
            success: `${REPORT_CONFIGS.find(r => r.id === reportType)?.name} exported successfully`,
            error: "Failed to export report"
          }
        });
      } else {
        toast({
          title: "No Data Available",
          description: `No data found for ${REPORT_CONFIGS.find(r => r.id === reportType)?.name}. Try adjusting filters or add more records.`,
          variant: "destructive"
        });
      }
    } finally {
      setGeneratingReport(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Reports & Analytics</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Comprehensive business intelligence and performance metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={reportPeriod} onValueChange={setReportPeriod}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            Reports
          </TabsTrigger>
          <TabsTrigger value="advanced" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Advanced
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Analytics Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Executive Summary KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {/* KPI tiles: $ tiles need canSeeFinancialReports, count/rate tiles need
                canSeeGeneralReports — an Accounting-only role (reports:financial +
                commissions:view, no reports:view) should only see the $ ones */}
            {canSeeFinancialReports && (
              <Card className="col-span-1">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900">
                      <DollarSign className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Annual Premium (YTD)</p>
                      <p className="text-lg font-bold" title={`$${toCents(reportMetrics.ytdPremiumValue)} billed since Jan 1`}>${(reportMetrics.ytdPremiumValue / 1000).toFixed(0)}K</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {canSeeGeneralReports && (
              <Card className="col-span-1">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Active Clients</p>
                      <p className="text-lg font-bold">{reportMetrics.activeClients}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {canSeeGeneralReports && (
              <Card className="col-span-1">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
                      <Users className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Enrollments ({periodLabel})</p>
                      <p className="text-lg font-bold">{reportMetrics.periodEnrollments}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {canSeeGeneralReports && (
              <Card className="col-span-1">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900">
                      <TrendingUp className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Conversion</p>
                      <p className="text-lg font-bold">{reportMetrics.conversionRate.toFixed(0)}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {canSeeGeneralReports && (
              <Card className="col-span-1">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-cyan-100 dark:bg-cyan-900">
                      <Target className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Pipeline</p>
                      <p className="text-lg font-bold">{reportMetrics.pendingQuotes}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {canSeeFinancialReports && (
              <Card className="col-span-1">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900">
                      <Percent className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Commission</p>
                      <p className="text-lg font-bold">${(reportMetrics.totalCommissions / 1000).toFixed(0)}K</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {canSeeGeneralReports && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <LineChartIcon className="h-4 w-4" />
                    Client Growth Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={clientGrowthData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="month" className="text-xs" />
                        <YAxis className="text-xs" />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                          labelFormatter={(label, payload) => payload?.[0]?.payload?.fullMonth || label}
                        />
                        <Area type="monotone" dataKey="cumulative" fill="#3B82F6" fillOpacity={0.1} stroke="#3B82F6" name="Total Clients" />
                        <Bar dataKey="clients" fill="#10B981" name="New Clients" radius={[4, 4, 0, 0]} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {canSeeFinancialReports && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Commission Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={commissionTrends}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="month" className="text-xs" />
                        <YAxis className="text-xs" tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                          formatter={(value) => [`$${Number(value).toLocaleString()}`, "Commission"]}
                        />
                        <Bar dataKey="total" fill="#10B981" name="Commission" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Second Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {canSeeFinancialReports && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <PieChartIcon className="h-4 w-4" />
                    Revenue by Plan Type
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={revenueByType}
                          cx="51%"
                          cy="42%"
                          innerRadius={33}
                          outerRadius={55}
                          dataKey="revenue"
                          nameKey="name"
                          // Tiny slices get no label — their labels pile up
                          // unreadably at the same point. The legend + tooltip
                          // still identify them.
                          label={({ name, percent }) =>
                            percent >= 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ""
                          }
                          labelLine={false}
                        >
                          {revenueByType.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `$${Number(value).toLocaleString()}`} />
                        <Legend
                          formatter={(value, entry: any) => {
                            const total = revenueByType.reduce((s, r) => s + r.revenue, 0);
                            const pct = total > 0 ? ((entry?.payload?.revenue ?? 0) / total) * 100 : 0;
                            return `${value} ${pct.toFixed(pct > 0 && pct < 1 ? 1 : 0)}%`;
                          }}
                          wrapperStyle={{ fontSize: "12px" }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* shows both a client count and a $/mo figure — treated as
                non-financial (operations), matching its Reports-tab sibling's
                category tag; the $/mo detail is secondary to renewal timing */}
            {canSeeGeneralReports && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Renewal Pipeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {renewalPipeline.map((bucket, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${idx === 0 ? 'bg-red-500' : idx === 1 ? 'bg-orange-500' : idx === 2 ? 'bg-yellow-500' : 'bg-green-500'}`} />
                          <span className="text-sm">{bucket.range}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-medium" data-testid={`renewal-pipeline-count-${idx}`}>{bucket.count} {pluralize(bucket.count, "client")}</span>
                          <span className="text-xs text-muted-foreground ml-2">${bucket.revenue.toLocaleString()}/mo</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {canSeeGeneralReports && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Sales Pipeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pipelineMetrics.map((stage, idx) => (
                      <div key={idx}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm">{stage.name}</span>
                          <span className="text-sm font-medium">{stage.count}</span>
                        </div>
                        <Progress value={(stage.count / Math.max(...pipelineMetrics.map(s => s.count), 1)) * 100} className="h-2" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Performance Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {canSeeFinancialReports && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Top Carriers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {carrierDistribution.slice(0, 4).map((carrier, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="truncate max-w-[120px]">{carrier.name}</span>
                        <span className="font-medium">${(carrier.revenue / 1000).toFixed(0)}K</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {canSeeGeneralReports && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Task Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Completed</span>
                      <span className="font-medium text-green-600">{reportMetrics.completedTasks}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Overdue</span>
                      <span className="font-medium text-red-600">{reportMetrics.overdueTasks}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Completion Rate</span>
                      <span className="font-medium">{reportMetrics.taskCompletionRate.toFixed(0)}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {canSeeFinancialReports && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Avg Premium</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Per Client</span>
                      <span className="font-medium">${reportMetrics.avgPremiumPerClient.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Per Employee</span>
                      <span className="font-medium">${reportMetrics.avgPremiumPerEmployee.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Monthly Total</span>
                      <span className="font-medium">${allTimeMonthlyPremiums.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {canSeeGeneralReports && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Activity This {periodLabel}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>New Clients</span>
                      <span className="font-medium text-blue-600">+{reportMetrics.newClientsThisPeriod}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>New Enrollments</span>
                      <span className="font-medium text-green-600">+{filteredEnrollments.length}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Quotes Sent</span>
                      <span className="font-medium">{reportMetrics.totalQuotes}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Standard Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          {/* Report Data Summary — no "Total Clients" tile, it'd duplicate Dashboard's "Active Clients" */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <Card className="col-span-1">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
                    <Briefcase className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Employees</p>
                    <p className="text-lg font-bold">{totalActiveEmployees}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="col-span-1">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-cyan-100 dark:bg-cyan-900">
                    <UserCheck className="h-5 w-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Enrollments</p>
                    <p className="text-lg font-bold">{reportMetrics.activeEnrollments}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="col-span-1">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900">
                    <FileText className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Commission Records</p>
                    <p className="text-lg font-bold">{commissions.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="col-span-1">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900">
                    <Shield className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Carriers</p>
                    <p className="text-lg font-bold">{activeClientPlanSummary.carriers}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="col-span-1">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-rose-100 dark:bg-rose-900">
                    <Heart className="h-5 w-5 text-rose-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Plans</p>
                    <p className="text-lg font-bold">{activeClientPlanSummary.plans}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Standard Reports</h3>
              <p className="text-sm text-muted-foreground">Generate and download comprehensive business reports</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {REPORT_CONFIGS.map((report) => {
              const Icon = report.icon;
              const isGenerating = generatingReport === report.id;
              const isLocked = report.category === "financial" ? !canSeeFinancialReports : !canSeeGeneralReports;

              return (
                <Card key={report.id} className="hover:border-primary/50 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className={`p-2 rounded-lg ${
                        report.category === "clients" ? "bg-blue-100 dark:bg-blue-900" :
                        report.category === "financial" ? "bg-green-100 dark:bg-green-900" :
                        "bg-purple-100 dark:bg-purple-900"
                      }`}>
                        <Icon className={`h-5 w-5 ${
                          report.category === "clients" ? "text-blue-600" :
                          report.category === "financial" ? "text-green-600" :
                          "text-purple-600"
                        }`} />
                      </div>
                      <Badge variant="outline" className="text-xs capitalize">{report.category}</Badge>
                    </div>
                    <CardTitle className="text-base mt-3">{report.name}</CardTitle>
                    <CardDescription className="text-xs line-clamp-2">{report.description}</CardDescription>
                  </CardHeader>
                  <CardFooter className="pt-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => generateReport(report.id)}
                      disabled={isGenerating || isLocked}
                    >
                      {isGenerating ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : isLocked ? (
                        <>
                          <Lock className="h-4 w-4 mr-2" />
                          No Access
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Export CSV
                        </>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Advanced Analytics Tab */}
        <TabsContent value="advanced" className="space-y-6">
          {!hasFeature("analytics_basic") ? (
            <div className="flex items-center justify-center min-h-[400px] p-8">
              <div className="max-w-md w-full space-y-6 text-center">
                <div className="flex justify-center">
                  <div className="rounded-full bg-amber-100 p-4">
                    <Lock className="h-8 w-8 text-amber-600" />
                  </div>
                </div>
                <div className="space-y-2">
                  <h1 className="text-2xl font-semibold text-gray-900">Upgrade Required</h1>
                  <p className="text-gray-600">
                    Advanced Analytics requires a Premium or Elite subscription tier.
                  </p>
                </div>
              </div>
            </div>
          ) : (
          <>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Crown className="h-5 w-5 text-amber-500" />
                Advanced Analytics
              </h3>
              <p className="text-sm text-muted-foreground">Elite tier analytics with predictive insights</p>
            </div>
            <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">Elite Feature</Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Retention Analysis */}
            <Card className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10" />
              <CardHeader className="relative">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                    <Shield className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Retention Analysis</CardTitle>
                    <CardDescription className="text-xs">Client churn prediction & risk scoring</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Retention Rate</span>
                  {advancedMetrics.retention == null ? (
                    <NotEnoughData reason={`No active or inactive clients started before this ${periodLabel}`} />
                  ) : (
                    <span
                      className="text-lg font-bold text-green-600"
                      title={`Clients that started before this ${periodLabel} and are still active`}
                    >
                      {advancedMetrics.retention.toFixed(1)}%
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">At-Risk Clients</span>
                  <NotEnoughData reason="No client risk scoring yet" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Churn Rate</span>
                  {advancedMetrics.retention == null ? (
                    <NotEnoughData reason={`No active or inactive clients started before this ${periodLabel}`} />
                  ) : (
                    <span
                      className="text-lg font-bold text-red-600"
                      title={`Clients that started before this ${periodLabel} and are now inactive`}
                    >
                      {(100 - advancedMetrics.retention).toFixed(1)}%
                    </span>
                  )}
                </div>
                <Progress value={advancedMetrics.retention ?? 0} className="h-2" />
              </CardContent>
            </Card>

            {/* Revenue Forecasting */}
            <Card className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-500/10" />
              <CardHeader className="relative">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Revenue Forecast</CardTitle>
                    <CardDescription className="text-xs">12-month projection based on renewals</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Projected Annual</span>
                  <span className="text-lg font-bold text-green-600" title="Current in-force monthly premium × 12">${(reportMetrics.annualPremiumValue / 1000).toFixed(0)}K</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Growth Rate</span>
                  {advancedMetrics.growth == null ? (
                    <NotEnoughData reason="Needs billed premium in an earlier month of this year" />
                  ) : (
                    <div
                      className={`flex items-center ${advancedMetrics.growth.rate < 0 ? "text-red-600" : "text-green-600"}`}
                      title={`Premium billed this month vs ${format(new Date(new Date().getFullYear(), advancedMetrics.growth.baseMonth, 1), "MMMM")}`}
                    >
                      {advancedMetrics.growth.rate < 0 ? <ArrowDownRight className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                      <span className="font-bold">{Math.abs(advancedMetrics.growth.rate).toFixed(1)}%</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Renewal Revenue</span>
                  <span className="text-lg font-bold">${(renewalPipeline.reduce((s, b) => s + b.revenue, 0) * 12 / 1000).toFixed(0)}K</span>
                </div>
              </CardContent>
            </Card>

            {/* Client Lifetime Value */}
            <Card className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10" />
              <CardHeader className="relative">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
                    <Calculator className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Client Lifetime Value</CardTitle>
                    <CardDescription className="text-xs">Expected revenue per client relationship</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Avg CLV</span>
                  <span className="text-lg font-bold text-purple-600">${(reportMetrics.avgPremiumPerClient * 36).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Avg Tenure</span>
                  {advancedMetrics.avgTenure == null ? (
                    <NotEnoughData reason="No active clients with a Client since date" />
                  ) : (
                    <span className="text-lg font-bold" title="Mean time since Client since, active clients">
                      {advancedMetrics.avgTenure.toFixed(1)} years
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Acquisition Cost</span>
                  <NotEnoughData reason="Marketing/sales spend isn't tracked" />
                </div>
              </CardContent>
            </Card>

            {/* Cross-Sell Opportunities */}
            <Card className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-amber-500/10" />
              <CardHeader className="relative">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900">
                    <Sparkles className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Cross-Sell Opportunities</CardTitle>
                    <CardDescription className="text-xs">Clients missing additional coverage</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4 text-amber-600" />
                    <span className="text-sm">No Vision</span>
                  </div>
                  <Badge variant="secondary">{advancedMetrics.crossSell.vision} {pluralize(advancedMetrics.crossSell.vision, "client")}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Heart className="h-4 w-4 text-red-600" />
                    <span className="text-sm">No Life</span>
                  </div>
                  <Badge variant="secondary">{advancedMetrics.crossSell.life} {pluralize(advancedMetrics.crossSell.life, "client")}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-blue-600" />
                    <span className="text-sm">No Disability</span>
                  </div>
                  <Badge variant="secondary">{advancedMetrics.crossSell.disability} {pluralize(advancedMetrics.crossSell.disability, "client")}</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Pipeline Velocity */}
            <Card className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10" />
              <CardHeader className="relative">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-cyan-100 dark:bg-cyan-900">
                    <Clock className="h-5 w-5 text-cyan-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Pipeline Velocity</CardTitle>
                    <CardDescription className="text-xs">Quote-to-close time analysis</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Avg Close Time</span>
                  {advancedMetrics.avgCloseDays == null ? (
                    <NotEnoughData reason="No prospects converted to clients yet" />
                  ) : (
                    <span className="text-lg font-bold" title="Prospect created → converted client's Client since">
                      {Math.round(advancedMetrics.avgCloseDays)} {pluralize(Math.round(advancedMetrics.avgCloseDays), "day")}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Pipeline Value</span>
                  <span className="text-lg font-bold text-cyan-600">${(quotes.filter(q => q.status === "sent").reduce((s, q) => s + Number((q as any).totalMonthlyPremium || 0), 0) * 12 / 1000).toFixed(0)}K</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Win Rate</span>
                  <span className="text-lg font-bold text-green-600">{reportMetrics.conversionRate.toFixed(0)}%</span>
                </div>
              </CardContent>
            </Card>

            {/* Agent Performance */}
            <Card className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-violet-500/10" />
              <CardHeader className="relative">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900">
                    <UserCheck className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Agent Scorecard</CardTitle>
                    <CardDescription className="text-xs">Individual producer performance</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Top Producer</span>
                  <span className="text-lg font-bold">—</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Avg Book Size</span>
                  {advancedMetrics.avgBookSize == null ? (
                    <NotEnoughData reason="No active clients" />
                  ) : (
                    <span className="text-lg font-bold" title="Total annual premium ÷ active clients">
                      ${(advancedMetrics.avgBookSize / 1000).toFixed(0)}K
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Quota Attainment</span>
                  <NotEnoughData reason="No producer quotas are set up" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Trend Analysis Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Year-over-Year Trend Analysis
              </CardTitle>
              <CardDescription>Premium growth and client acquisition trends</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={clientGrowthData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="month" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                    <Legend />
                    <Line type="monotone" dataKey="cumulative" stroke="#3B82F6" strokeWidth={2} name="Total Clients" dot={false} />
                    <Line type="monotone" dataKey="clients" stroke="#10B981" strokeWidth={2} name="New Clients" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {tier !== "elite" && (
            <Card className="border-2 border-dashed border-amber-300 dark:border-amber-700 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
              <CardContent className="flex flex-col md:flex-row items-center justify-between gap-4 p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-gradient-to-r from-amber-400 to-orange-400">
                    <Crown className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Unlock Full Advanced Analytics</h3>
                    <p className="text-sm text-muted-foreground">Get AI-powered predictions, custom dashboards, and automated reporting</p>
                  </div>
                </div>
                <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Upgrade to Elite
                </Button>
              </CardContent>
            </Card>
          )}
          </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
